import {
  useCancelConversation,
  useCancelConversationOccurrence,
  useRescheduleConversation,
} from "@coaching-culture/api-client";
import { Box, Button, Flex, FormInput, ReactModal, Text } from "@coaching-culture/ui";
import { useUser } from "auth";
import { fromUnixTime, getUnixTime } from "date-fns";
import { useState } from "react";
import ReactDatePicker from "react-datepicker";
import { CancelConversationModal } from "./CancelConversationModal";
import { ConvoAndInstance, isCancelled, isCompleted } from "./conversationList";

/**
 * Reschedule and cancel for one occurrence: who may do them, and the
 * modals that do them. Render `modals` wherever the hook is used.
 */
export const useConversationActions = (convo: ConvoAndInstance) => {
  const [user] = useUser();
  const [cancelling, setCancelling] = useState(false);
  const [rescheduling, setRescheduling] = useState(false);
  const [date, setDate] = useState<Date>(new Date());
  const [location, setLocation] = useState(convo.location);
  const [duration, setDuration] = useState(convo.duration);

  const rescheduleConvo = useRescheduleConversation(convo.id, convo.index);
  const cancelConversation = useCancelConversation(convo.id);
  const cancelOccurrence = useCancelConversationOccurrence(
    convo.id,
    convo.index,
  );

  const isManager = convo.manager.id === user.id;
  const isOwner = convo.createdBy === user.id;
  const open = !isCompleted(convo) && !isCancelled(convo);

  const canCancel = open && isManager;
  const canReschedule =
    open && (isOwner || isManager || convo.allowReschedulingByDirectReport);

  const startReschedule = () => {
    setDate(fromUnixTime(convo.scheduledDate));
    setLocation(convo.location);
    setDuration(convo.duration);
    setRescheduling(true);
  };

  const reschedule = async () => {
    if (getUnixTime(date) === convo.scheduledDate) {
      setRescheduling(false);
      return;
    }
    await rescheduleConvo.mutateAsync({
      scheduledDate: getUnixTime(date),
      location,
      duration,
    });
    setRescheduling(false);
  };

  const confirmCancellation = async (all: boolean, reason: string) => {
    setCancelling(false);
    if (all) {
      await cancelConversation.mutateAsync({ reason });
    } else {
      await cancelOccurrence.mutateAsync({ reason });
    }
  };

  const modals = (
    <>
      <CancelConversationModal
        isOpen={cancelling}
        onCancel={() => setCancelling(false)}
        onConfirm={confirmCancellation}
      />
      <ReactModal onClose={() => setRescheduling(false)} isOpen={rescheduling}>
        <Box p={4}>
          <Text mb={3} fontSize="section" fontWeight={600}>
            Reschedule conversation
          </Text>
          <div style={{ width: "100%", marginBottom: 12 }}>
            <ReactDatePicker
              selected={date}
              onChange={setDate}
              inline
              showTimeSelect
              timeIntervals={15}
            />
          </div>
          <FormInput
            label="Duration (minutes)"
            value={duration}
            onChange={(ev) => setDuration(ev.target.valueAsNumber)}
            type="number"
          />
          <FormInput
            label="Location"
            value={location}
            onChange={(ev) => setLocation(ev.target.value)}
          />
          <Flex justifyContent="center" mt={3} style={{ gap: 8 }}>
            <Button onClick={() => setRescheduling(false)}>Cancel</Button>
            <Button variant="primary" onClick={reschedule}>
              Save
            </Button>
          </Flex>
        </Box>
      </ReactModal>
    </>
  );

  return {
    modals,
    canCancel,
    canReschedule,
    startCancel: () => setCancelling(true),
    startReschedule,
  };
};
